/**
 * Curated gazetteer: coordinates for the service area (Wicklow + south Dublin).
 *
 * Two kinds of entry, distinguished automatically at seed time (NOT by a flag
 * here): a name that matches an airport grid town becomes an in-grid anchor for
 * the distance model; everything else is an off-grid place we ESTIMATE.
 *
 * GRID-TOWN NAMES MUST MATCH docs/pricing.json EXACTLY — the seed slugifies the
 * name to a location_id, and that id must line up with the rate rows.
 *
 * Coordinates are hand-curated to ~town-centre precision. The airport model is
 * fit to grid prices by distance *rank*, so small coordinate error is absorbed;
 * every off-grid result is an estimate Helen confirms or overrides anyway.
 */

export type GazetteerEntry = {
  /** Town name. For grid towns, EXACTLY as it appears in pricing.json. */
  name: string;
  lat: number;
  lng: number;
  region: string;
  /**
   * Other names this SAME place answers to (T385): the Irish-shortform class,
   * where the price list carries the locals' short name but a customer types
   * the full one. An alias resolves to this entry's OWN grid row (same
   * location_id — never a new row, which would mint a different slug and land
   * on the off-grid estimate path). Matching is the same case-insensitive
   * containment the labels use — declared data, not inference; correcting a
   * wrong alias is a one-line edit here.
   */
  aliases?: readonly string[];
  /**
   * What a CUSTOMER should read, when the canonical `name` is a local short
   * form (T539 defect 536). Defaults to `name`.
   *
   * This exists because `name` is a TRIPLE-duty key — match key, pricing.json
   * join key AND rendered label — so it cannot be renamed to the formal name
   * without minting a different slug and dropping the journey onto the
   * off-grid estimate path. Read by the SITE codegen only
   * (bin/gen-site-inquiry-schema.ts); the kiosk deliberately keeps rendering
   * the canonical label (locations-repo.spec.ts: "an alias never renders").
   */
  display?: string;
};

/** Dublin Airport — the radial reference point for the airport grid. */
export const DUBLIN_AIRPORT = { lat: 53.4264, lng: -6.2499 } as const;

/** The airport journey's implicit destination label (T400 rework 1). Walk-in
 *  kiosk quotes have no terminal picker, so the generic airport names the
 *  destination end; inquiry journeys carry the customer's real terminal text
 *  and never fall back to this. */
export const DUBLIN_AIRPORT_LABEL = 'Dublin Airport';

export const GAZETTEER: readonly GazetteerEntry[] = [
  // --- Airport grid towns (names mirror pricing.json) -----------------------
  { name: 'Arklow', lat: 52.7931, lng: -6.1417, region: 'wicklow' },
  { name: 'Ashford', lat: 52.998, lng: -6.103, region: 'wicklow' },
  { name: 'Aughrim / Macreddin (Brooklodge)', lat: 52.8525, lng: -6.3289, region: 'wicklow' },
  { name: 'Avoca', lat: 52.8606, lng: -6.2153, region: 'wicklow' },
  { name: 'Ballinaclash', lat: 52.8939, lng: -6.2386, region: 'wicklow' },
  { name: 'Barndarrig', lat: 52.8889, lng: -6.145, region: 'wicklow' },
  { name: 'Bray', lat: 53.2026, lng: -6.1119, region: 'north-wicklow' },
  { name: 'Enniskerry', lat: 53.1925, lng: -6.1717, region: 'north-wicklow' },
  { name: 'Glenealy', lat: 52.9636, lng: -6.1747, region: 'wicklow' },
  // 'Charlesland' is a townland + ~1,500-home development on the SOUTHSIDE of
  // Greystones (T539 defect 534) — advertised in site.ts areasServed and in the
  // LocalBusiness schema, but absent from pricing.json, the gazetteer and the
  // site datalist, so a Charlesland customer could not find their own address.
  // Declared as an ALIAS deliberately: it resolves to Greystones' OWN grid row
  // (same location_id, same grid price), so this fixes discovery without minting
  // a new slug, a new rate row, or any commercial decision.
  { name: 'Greystones', lat: 53.1448, lng: -6.0633, region: 'north-wicklow', aliases: ['Charlesland'] },
  { name: 'Delgany', lat: 53.131, lng: -6.09, region: 'north-wicklow' },
  { name: 'Kilcoole', lat: 53.103, lng: -6.063, region: 'north-wicklow' },
  { name: 'Kilmacanogue', lat: 53.1719, lng: -6.1431, region: 'north-wicklow' },
  { name: 'Kilpedder', lat: 53.1208, lng: -6.1019, region: 'north-wicklow' },
  { name: 'Laragh', lat: 53.0072, lng: -6.2986, region: 'wicklow' },
  { name: 'Newcastle', lat: 53.0744, lng: -6.0494, region: 'north-wicklow' },
  // 'Newtown' is what locals call Newtownmountkennedy — the coordinates here
  // ARE NTMK's, and the price list sandwiches it exactly on NTMK's geography
  // (T383 §3). CONFIRMED by Alex 2026-07-28 (T539 defect 536): the alias is
  // correct, so the Owen-confirm question is CLOSED — do not re-raise it.
  //
  // `name` stays 'Newtown' because it is the pricing.json join key; `display`
  // gives the customer the formal name on the site. The kiosk still renders
  // 'Newtown' by design. A local typing "Newtown" still finds it, because the
  // site filter is substring-on-folded and 'newtown' ⊂ 'newtownmountkennedy'.
  { name: 'Newtown', lat: 53.0908, lng: -6.1108, region: 'north-wicklow', aliases: ['Newtownmountkennedy'], display: 'Newtownmountkennedy' },
  { name: 'Rathdrum', lat: 52.9272, lng: -6.2306, region: 'wicklow' },
  { name: 'Rathnew', lat: 52.991, lng: -6.066, region: 'wicklow' },
  { name: 'Redcross', lat: 52.8722, lng: -6.1483, region: 'wicklow' },
  { name: 'Roundwood', lat: 53.0667, lng: -6.2236, region: 'wicklow' },
  { name: 'Shillelagh', lat: 52.7556, lng: -6.5339, region: 'south-wicklow' },
  { name: 'Tinahely', lat: 52.7969, lng: -6.4631, region: 'south-wicklow' },
  { name: 'Wicklow Town', lat: 52.9808, lng: -6.0445, region: 'wicklow' },
  { name: 'Brittas Bay', lat: 52.8736, lng: -6.0739, region: 'wicklow' },
  { name: 'Glenmalure', lat: 52.9533, lng: -6.3464, region: 'wicklow' },
  { name: 'Glendalough', lat: 53.0106, lng: -6.3275, region: 'wicklow' },
  { name: 'Ballybeg House', lat: 52.92, lng: -6.55, region: 'west-wicklow' },

  // --- Off-grid places we estimate (west Wicklow + south Dublin) -------------
  { name: 'Baltinglass', lat: 52.9417, lng: -6.7081, region: 'west-wicklow' },
  { name: 'Blessington', lat: 53.1717, lng: -6.5378, region: 'west-wicklow' },
  { name: 'Dunlavin', lat: 53.0533, lng: -6.6975, region: 'west-wicklow' },
  { name: 'Donard', lat: 53.0228, lng: -6.6128, region: 'west-wicklow' },
  { name: 'Hollywood', lat: 53.0958, lng: -6.5878, region: 'west-wicklow' },
  { name: 'Kilbride', lat: 53.18, lng: -6.45, region: 'west-wicklow' },
  { name: 'Carnew', lat: 52.705, lng: -6.505, region: 'south-wicklow' },
  { name: 'Cabinteely', lat: 53.262, lng: -6.149, region: 'south-dublin' },
  { name: 'Shankill', lat: 53.236, lng: -6.124, region: 'south-dublin' },
  { name: 'Dún Laoghaire', lat: 53.294, lng: -6.134, region: 'south-dublin' },
  { name: 'Sandyford', lat: 53.278, lng: -6.224, region: 'south-dublin' },
  { name: 'Stepaside', lat: 53.249, lng: -6.214, region: 'south-dublin' },
  { name: 'Cherrywood', lat: 53.245, lng: -6.139, region: 'south-dublin' },
  { name: 'Sallynoggin', lat: 53.278, lng: -6.135, region: 'south-dublin' },
] as const;

/**
 * Canonical gazetteer names whose declared alias CONTAINS the query
 * (case-insensitive — the same containment predicate the labels use, so
 * partials like 'Newtownmount' work). Pure data lookup over the registry;
 * the matcher runs it only inside its zero-result fallback chain, so an
 * alias can never remove, reorder or shadow a real label match.
 */
export function aliasCanonicalNames(query: string): string[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [];
  const out: string[] = [];
  for (const g of GAZETTEER) {
    const aliases: readonly string[] = (g as GazetteerEntry).aliases ?? [];
    if (aliases.some((a) => a.toLowerCase().includes(q)) && !out.includes(g.name)) {
      out.push(g.name);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Eircode routing keys (T385 item 5) — suggestion-grade AREA data, never a
// resolver. An Post defines routing keys per delivery route, not geography,
// so any key→towns map is an approximation (A63 alone spans 6+ towns); the
// kiosk uses this ONLY to hint and to order suggestions — it never removes
// results and never auto-picks. Data-driven rows: correcting one is a
// one-line edit; a key not listed here simply shows no hint.
// ---------------------------------------------------------------------------

export type RoutingKeyArea = {
  /** The routing key — the first 3 characters of a full Eircode. */
  key: string;
  /** Short human label for the area the key roughly covers. */
  areaLabel: string;
  /** Gazetteer names (canonical, EXACT) the key is known to cover. */
  towns: readonly string[];
};

// Every row below is SOURCED (T383 §6a.4 or the T385 research pass —
// Wikipedia routing-area list + per-town pages, autoaddress.com). Rows are
// deliberately incomplete rather than guessed: an unmapped key simply shows
// no hint. Keys may overlap (delivery routes, not polygons). Towns our
// research could NOT source stay out until someone sources them (dropped as
// unverified: Kilpedder, Enniskerry, Kilmacanogue, Glenealy, Brittas Bay,
// Redcross, Barndarrig; Rathdrum/Glendalough/Laragh + the south-west and
// west-Wicklow clusters have no sourced key at all yet).
export const ROUTING_KEY_AREAS: readonly RoutingKeyArea[] = [
  // T383 §6a.4 (attested): A63 spans 6+ towns — the north-Wicklow coastal strip.
  { key: 'A63', areaLabel: 'north Wicklow coastal area', towns: ['Newtown', 'Greystones', 'Delgany', 'Kilcoole', 'Newcastle', 'Rathnew', 'Wicklow Town'] },
  // Roundwood under the Bray key is surprising but sourced (en.wikipedia.org/wiki/Roundwood).
  { key: 'A98', areaLabel: 'Bray area', towns: ['Bray', 'Roundwood'] },
  { key: 'A67', areaLabel: 'Wicklow town area', towns: ['Wicklow Town', 'Rathnew', 'Ashford'] },
  { key: 'Y14', areaLabel: 'Arklow area', towns: ['Arklow', 'Avoca'] },
  { key: 'A96', areaLabel: 'Dún Laoghaire area', towns: ['Dún Laoghaire', 'Sallynoggin'] },
  { key: 'D18', areaLabel: 'south Dublin area', towns: ['Cabinteely', 'Shankill', 'Sandyford', 'Cherrywood', 'Stepaside'] },
] as const;

/**
 * Derive the routing key from a (possibly partial/messy) Eircode string.
 * Format: the first block is a letter + 2 digits ('A63'), with 'D6W' the one
 * historic exception — spaced or unspaced full codes both work. Returns null
 * when the string doesn't start like an Eircode — a hint must never be
 * manufactured from noise.
 */
export function routingKeyOf(eircode: string | null | undefined): string | null {
  const m = (eircode ?? '').trim().toUpperCase().match(/^(D6W|[A-Z]\d{2})/);
  return m ? m[1]! : null;
}

/** The declared area row for a routing key, or null when unmapped. */
export function areaForRoutingKey(key: string | null | undefined): RoutingKeyArea | null {
  if (!key) return null;
  const k = key.trim().toUpperCase();
  return ROUTING_KEY_AREAS.find((a) => a.key === k) ?? null;
}
